'use client';
import React from 'react';
import { AND_NEW_ID, EXPRESSION_NEW_ID, IdTreeExpression, NOT_NEW_ID, OR_NEW_ID } from './entities';
import { SearchModule } from '../Modules';
import { SearchItem } from '@benzinga/search-modules';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import styled, { css } from '@benzinga/themetron';
import { ExpressionToken, SearchTag, SearchTagIcon } from '../SearchBar';
import { attachNode, detachNode, getParentRelativeIndex, newIncompleteExpressionNode } from './TreeOperations';
import { Tooltip } from '@benzinga/core-ui';
import { TokenizedToTree, TreeToIdTree } from './helpers';
import { TagSearch } from '../Tag';
import { Close, Edit } from '@benzinga/themed-icons';

export interface ExpressionBuilderSectionProps {
  node: IdTreeExpression;
  parentNode: IdTreeExpression | undefined;
  root: IdTreeExpression;
  onExpressionChange: (expression: IdTreeExpression | undefined) => void;
  modules: SearchModule[];
  draggedClone?: boolean;
}

const andColor = '#911587';
const orColor = '#821cc6';
const notColor = '#841d1d';
const expressionColor = '#008fd9';

export const ExpressionBuilderSectionAnd: React.FC<ExpressionBuilderSectionProps> = props => {
  if (props.node.operator !== 'and') {
    throw new Error(
      `ExpressionBuilderSectionAnd Error: Node not found or not an AND node ${props.node}, ${props.root}`,
    );
  }

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
  } = useDraggable({
    disabled: props.draggedClone,
    id: props.node.uuid,
  });
  const { setNodeRef: setDroppableRef } = useDroppable({
    disabled: props.draggedClone,
    id: props.node.uuid,
  });

  const toggleToOr = React.useCallback(() => {
    if (props.node.uuid === AND_NEW_ID) {
      return;
    }
    props.node.operator = 'or';
    const onExpressionChange = props.onExpressionChange;
    onExpressionChange(props.root);
  }, [props.node, props.onExpressionChange, props.root]);

  const removeNode = React.useCallback(() => {
    const onExpressionChange = props.onExpressionChange;
    if (!props.parentNode) {
      onExpressionChange(undefined);
      return;
    }
    let root: IdTreeExpression | undefined = props.root;
    root = detachNode(props.root, props.node, props.parentNode);
    onExpressionChange(root);
  }, [props.node, props.onExpressionChange, props.parentNode, props.root]);

  const innerSections = props.node.exprs.map(childNode => (
    <ExpressionBuilderSection
      draggedClone={props.draggedClone}
      key={childNode.uuid}
      modules={props.modules}
      node={childNode}
      onExpressionChange={props.onExpressionChange}
      parentNode={props.node}
      root={props.root}
    />
  ));
  return (
    <div ref={props.node.uuid === AND_NEW_ID ? null : setDroppableRef}>
      <SectionWrapper ref={setDraggableRef} sectionColor={andColor} {...listeners} {...attributes}>
        <SectionHeading isGhost={props.node.isGhost} onDoubleClick={toggleToOr}>
          <div
            style={{
              display: 'inline-block',
              flexGrow: 1,
              textAlign: 'center',
            }}
          >
            <Tooltip content={'Double click to toggle'} position={'bottom'}>
              {'AND'}
            </Tooltip>
          </div>
          {props.node.uuid !== AND_NEW_ID && (
            <Close
              hoverable
              onClick={removeNode}
              style={{
                bottom: '3px',
                cursor: 'pointer',
                fontSize: '8px',
                justifySelf: 'end',
                position: 'fixed',
                right: '2.5px',
              }}
            />
          )}
        </SectionHeading>
        <SectionContent>{innerSections}</SectionContent>
      </SectionWrapper>
    </div>
  );
};

export const ExpressionBuilderSectionOr: React.FC<ExpressionBuilderSectionProps> = props => {
  if (props.node.operator !== 'or') {
    throw new Error(`ExpressionBuilderSectionOr Error: Node not found or not an OR node ${props.node}, ${props.root}`);
  }

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
  } = useDraggable({
    disabled: props.draggedClone,
    id: props.node.uuid,
  });
  const { setNodeRef: setDroppableRef } = useDroppable({
    disabled: props.draggedClone,
    id: props.node.uuid,
  });

  const toggleToAnd = React.useCallback(() => {
    if (props.node.uuid === OR_NEW_ID) {
      return;
    }
    props.node.operator = 'and';
    const onExpressionChange = props.onExpressionChange;
    onExpressionChange(props.root);
  }, [props.node, props.onExpressionChange, props.root]);

  const removeNode = React.useCallback(() => {
    const onExpressionChange = props.onExpressionChange;
    if (!props.parentNode) {
      onExpressionChange(undefined);
      return;
    }
    let root: IdTreeExpression | undefined = props.root;
    root = detachNode(props.root, props.node, props.parentNode);
    onExpressionChange(root);
  }, [props.node, props.onExpressionChange, props.parentNode, props.root]);

  const innerSections = props.node.exprs.map(childNode => (
    <ExpressionBuilderSection
      draggedClone={props.draggedClone}
      key={childNode.uuid}
      modules={props.modules}
      node={childNode}
      onExpressionChange={props.onExpressionChange}
      parentNode={props.node}
      root={props.root}
    />
  ));
  return (
    <div ref={props.node.uuid === OR_NEW_ID ? null : setDroppableRef}>
      <SectionWrapper ref={setDraggableRef} sectionColor={orColor} {...listeners} {...attributes}>
        <SectionHeading isGhost={props.node.isGhost} onDoubleClick={toggleToAnd}>
          <div
            style={{
              display: 'inline-block',
              flexGrow: 1,
              textAlign: 'center',
            }}
          >
            <Tooltip content={'Double click to toggle'} position={'bottom'}>
              {'OR'}
            </Tooltip>
          </div>
          {props.node.uuid !== OR_NEW_ID && (
            <Close
              hoverable
              onClick={removeNode}
              style={{
                bottom: '3px',
                cursor: 'pointer',
                fontSize: '8px',
                justifySelf: 'end',
                position: 'fixed',
                right: '2.5px',
              }}
            />
          )}
        </SectionHeading>
        <SectionContent>{innerSections}</SectionContent>
      </SectionWrapper>
    </div>
  );
};

export const ExpressionBuilderSectionNot: React.FC<ExpressionBuilderSectionProps> = props => {
  if (props.node.operator !== 'not') {
    throw new Error(
      `ExpressionBuilderSectionNot Error: Node not found or not an NOT node ${props.node}, ${props.root}`,
    );
  }

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
  } = useDraggable({
    disabled: props.draggedClone,
    id: props.node.uuid,
  });
  const { setNodeRef: setDroppableRef } = useDroppable({
    disabled: props.draggedClone,
    id: props.node.uuid,
  });

  const removeNode = React.useCallback(() => {
    const onExpressionChange = props.onExpressionChange;
    let root: IdTreeExpression | undefined = props.root;

    if (props.node.operator !== 'not') {
      return;
    }

    if (props.node.expr) {
      const childNode = props.node.expr;
      root = detachNode(root, childNode, props.node);
      if (root && props.parentNode) {
        const relativeIndex = getParentRelativeIndex(props.root, props.node, props.parentNode);
        root = detachNode(root, props.node, props.parentNode) ?? props.parentNode;
        root = attachNode(root, childNode, props.parentNode, relativeIndex);
      } else {
        root = childNode;
      }
    } else {
      if (!props.parentNode) {
        root = undefined;
      } else {
        root = detachNode(root, props.node, props.parentNode);
      }
    }
    onExpressionChange(root);
  }, [props.node, props.onExpressionChange, props.parentNode, props.root]);

  const innerSections = props.node.expr ? (
    <ExpressionBuilderSection
      draggedClone={props.draggedClone}
      key={props.node.expr.uuid}
      modules={props.modules}
      node={props.node.expr}
      onExpressionChange={props.onExpressionChange}
      parentNode={props.node}
      root={props.root}
    />
  ) : null;
  return (
    <div ref={props.node.uuid === NOT_NEW_ID ? null : setDroppableRef}>
      <NotSectionWrapper
        hasChildren={Boolean(innerSections)}
        ref={setDraggableRef}
        sectionColor={notColor}
        {...listeners}
        {...attributes}
      >
        <NotSectionHeading>
          <div
            style={{
              display: 'inline-block',
              flexGrow: 1,
              textAlign: 'center',
            }}
          >
            {'NOT'}
          </div>
          {props.node.uuid !== NOT_NEW_ID && (
            <Close
              hoverable
              onClick={removeNode}
              style={{
                bottom: '3px',
                cursor: 'pointer',
                fontSize: '8px',
                justifySelf: 'end',
                position: 'fixed',
                right: '2.5px',
              }}
            />
          )}
        </NotSectionHeading>
        <NotSectionContent>{innerSections}</NotSectionContent>
      </NotSectionWrapper>
    </div>
  );
};

export const ExpressionBuilderSectionExpression: React.FC<ExpressionBuilderSectionProps> = props => {
  const node = props.node;
  if (node.operator !== 'expression') {
    throw new Error(
      `ExpressionBuilderSectionExpression Error: Node not found or not an EXPRESSION node ${node}, ${props.root}`,
    );
  }

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
  } = useDraggable({
    disabled: props.draggedClone,
    id: node.uuid,
  });
  const { setNodeRef: setDroppableRef } = useDroppable({
    disabled: props.draggedClone,
    id: node.uuid,
  });

  const renderInfo = (() => {
    for (const module of props.modules) {
      const renderer = module.tag.render(node as SearchItem);
      if (renderer) {
        return { module, renderer };
      }
    }
    return undefined;
  })();

  const removeNode = React.useCallback(() => {
    const onExpressionChange = props.onExpressionChange;
    if (!props.parentNode) {
      onExpressionChange(undefined);
      return;
    }
    let root: IdTreeExpression | undefined = props.root;
    root = detachNode(props.root, node, props.parentNode);
    onExpressionChange(root);
  }, [node, props.onExpressionChange, props.parentNode, props.root]);

  const toggleEditNode = React.useCallback(() => {
    const onExpressionChange = props.onExpressionChange;
    const parentRelativeIndex = props.parentNode ? getParentRelativeIndex(props.root, node, props.parentNode) : 0;
    const new_root = detachNode(props.root, node, props.parentNode);
    const new_node = newIncompleteExpressionNode(node.moduleId, node, node.uuid);
    if (!new_root || !props.parentNode) {
      onExpressionChange(new_node);
    } else {
      attachNode(new_root, new_node, props.parentNode, parentRelativeIndex);
      onExpressionChange(new_root);
    }
  }, [props.onExpressionChange, props.parentNode, props.root, node]);

  switch (renderInfo?.renderer.type) {
    case 'text':
      return (
        <div ref={setDroppableRef}>
          <SectionWrapper ref={setDraggableRef} sectionColor={expressionColor} {...listeners} {...attributes}>
            <SectionHeading isGhost={node.isGhost}>{'  '}</SectionHeading>
            <ExpressionSectionContent isGhost={node.isGhost}>
              <div style={{ display: 'flex', flexDirection: 'row', minWidth: '150px' }}>
                <ExpressionSectionButtonWrapper>
                  <Close
                    hoverable
                    onClick={removeNode}
                    style={{ cursor: 'pointer', marginLeft: '4px', marginRight: '4px' }}
                  />
                  <Edit hoverable onClick={toggleEditNode} style={{ cursor: 'pointer', marginRight: '6px' }} />
                </ExpressionSectionButtonWrapper>
                <div style={{ fontWeight: 'bold' }}>{renderInfo.module.id.toUpperCase()}</div>
                <ExpressionSpacer />
                <div>
                  <SearchTag>
                    {renderInfo.renderer.icon ? <SearchTagIcon>{renderInfo.renderer.icon}</SearchTagIcon> : undefined}{' '}
                    {renderInfo.renderer.text}
                  </SearchTag>
                </div>
              </div>
            </ExpressionSectionContent>
          </SectionWrapper>
        </div>
      );
    case 'custom':
      return (
        <div ref={setDroppableRef}>
          <SectionWrapper ref={setDraggableRef} sectionColor={expressionColor} {...listeners} {...attributes}>
            <SectionHeading isGhost={node.isGhost}>{'  '}</SectionHeading>
            <ExpressionSectionContent isGhost={node.isGhost}>
              <div style={{ display: 'flex', flexDirection: 'row', minWidth: '150px' }}>
                <ExpressionSectionButtonWrapper>
                  <Close
                    hoverable
                    onClick={removeNode}
                    style={{ cursor: 'pointer', marginLeft: '4px', marginRight: '4px' }}
                  />
                  <Edit hoverable onClick={toggleEditNode} style={{ cursor: 'pointer', marginRight: '6px' }} />
                </ExpressionSectionButtonWrapper>
                <div style={{ fontWeight: 'bold' }}>{renderInfo.module.id.toUpperCase()}</div>
                <ExpressionSpacer />
                <div>{renderInfo.renderer.jsx}</div>
              </div>
            </ExpressionSectionContent>
          </SectionWrapper>
        </div>
      );
  }
  return <div>expression</div>;
};
export const ExpressionBuilderSectionIncompleteExpression: React.FC<ExpressionBuilderSectionProps> = props => {
  const node = props.node;
  if (node.operator !== 'incomplete_expression') {
    throw new Error(
      `ExpressionBuilderSectionIncompleteExpression Error: Node not found or not an INCOMPLETE_EXPRESSION node ${node}, ${props.root}`,
    );
  }

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
  } = useDraggable({
    disabled: props.draggedClone,
    id: node.uuid,
  });
  const { setNodeRef: setDroppableRef } = useDroppable({
    disabled: props.draggedClone,
    id: node.uuid,
  });

  const removeNode = React.useCallback(() => {
    const onExpressionChange = props.onExpressionChange;
    if (!props.parentNode) {
      onExpressionChange(undefined);
      return;
    }
    let root: IdTreeExpression | undefined = props.root;
    root = detachNode(props.root, node, props.parentNode);
    onExpressionChange(root);
  }, [node, props.onExpressionChange, props.parentNode, props.root]);

  const toggleEditNode = React.useCallback(() => {
    if (!node.fallbackExpression) {
      throw new Error('ExpressionBuilderSectionIncompleteExpression Error: Node does not have fallback expression');
    }
    const onExpressionChange = props.onExpressionChange;
    const parentRelativeIndex = props.parentNode ? getParentRelativeIndex(props.root, node, props.parentNode) : 0;
    const new_root = detachNode(props.root, node, props.parentNode);
    const new_node = node.fallbackExpression;
    if (!new_root || !props.parentNode) {
      onExpressionChange(new_node);
    } else {
      attachNode(new_root, new_node, props.parentNode, parentRelativeIndex);
      onExpressionChange(new_root);
    }
  }, [props.onExpressionChange, props.parentNode, props.root, node]);

  const itemRef = React.useRef<HTMLDivElement | null>(null);
  const stopPropagation = React.useCallback((e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const noTags = React.useMemo(() => [], []);
  const onTagsUpdated = React.useCallback(
    (tags: SearchItem[]) => {
      if (tags.length === 0) {
        return;
      }
      const onExpressionChange = props.onExpressionChange;
      const new_node = TreeToIdTree(TokenizedToTree([{ ...tags[0], operator: 'expression' } as ExpressionToken]));
      const parentRelativeIndex = props.parentNode ? getParentRelativeIndex(props.root, node, props.parentNode) : 0;
      const root = detachNode(props.root, node, props.parentNode);
      const new_root = (() => {
        if (!root || !props.parentNode) {
          return new_node;
        } else {
          if (new_node) {
            attachNode(root, new_node, props.parentNode, parentRelativeIndex);
          }
          return root;
        }
      })();
      onExpressionChange(new_root);
    },
    [node, props.onExpressionChange, props.parentNode, props.root],
  );

  return (
    <div ref={props.node.uuid.startsWith(EXPRESSION_NEW_ID) ? null : setDroppableRef}>
      <SectionWrapper ref={setDraggableRef} sectionColor={expressionColor} {...attributes} {...listeners}>
        <SectionHeading isGhost={node.isGhost}>{'  '}</SectionHeading>
        <ExpressionSectionContent isGhost={node.isGhost}>
          <div
            style={{ alignItems: 'center', display: 'flex', flexDirection: 'row', height: '100%', minWidth: '150px' }}
          >
            {!node.uuid.startsWith(EXPRESSION_NEW_ID) && (
              <ExpressionSectionButtonWrapper>
                <Close
                  hoverable
                  onClick={removeNode}
                  style={{ cursor: 'pointer', marginLeft: '4px', marginRight: '4px' }}
                />
                {node.fallbackExpression && (
                  <Edit hoverable onClick={toggleEditNode} style={{ cursor: 'pointer', marginRight: '6px' }} />
                )}
              </ExpressionSectionButtonWrapper>
            )}
            <div style={{ fontWeight: 'bold' }}>{node.moduleId.toUpperCase()}</div>
            <ExpressionSpacer />
            {!node.uuid.startsWith(EXPRESSION_NEW_ID) && (
              <SearchDiv
                ref={r => {
                  itemRef.current?.removeEventListener('mousedown', stopPropagation);
                  itemRef.current = r;
                  itemRef.current?.addEventListener('mousedown', stopPropagation);
                }}
              >
                <SearchDivAbs>
                  <StyledTagSearch
                    modules={props.modules.filter(module => module.id === node.moduleId)}
                    onTagsUpdated={onTagsUpdated}
                    placeholder="Select replacement item"
                    tags={noTags}
                    thinUI={true}
                  />
                </SearchDivAbs>
              </SearchDiv>
            )}
          </div>
        </ExpressionSectionContent>
      </SectionWrapper>
    </div>
  );
};

export const ExpressionBuilderSection: React.FC<ExpressionBuilderSectionProps> = props => {
  switch (props.node.operator) {
    case 'and':
      return <ExpressionBuilderSectionAnd {...props} />;
    case 'or': {
      return <ExpressionBuilderSectionOr {...props} />;
    }
    case 'not': {
      return <ExpressionBuilderSectionNot {...props} />;
    }
    case 'expression': {
      return <ExpressionBuilderSectionExpression {...props} />;
    }
    case 'incomplete_expression': {
      return <ExpressionBuilderSectionIncompleteExpression {...props} />;
    }
  }
};

const SectionHeading = styled.div<{ isGhost?: boolean }>`
  color: white;
  display: flex;
  writing-mode: vertical-lr;
  transform: rotate(180deg);
  padding: 8px 0 8px 0;
  min-width: 9px;
  font-size: 10px;
  font-weight: bold;
  cursor: grab;
  ${({ isGhost }) =>
    isGhost &&
    css`
      opacity: 0.6;
    `}
`;

const SectionContent = styled.div`
  border: solid 2px ${props => props.theme.colors.borderDark};
  background-color: ${props => props.theme.colors.backgroundInactive};
  border-left: none;
  padding: 4px 8px 4px 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  flex-grow: 1;
`;

const SectionWrapper = styled.div<{ sectionColor: string; isNodeNode?: boolean }>`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  font-size: 12px;
  min-height: 50px;
  user-select: none;
  padding-top: 4px;
  padding-bottom: 4px;
  & > ${SectionHeading} {
    background-color: ${({ sectionColor }) => sectionColor};
  }
`;

const NotSectionContent = styled(SectionContent)``;

const NotSectionHeading = styled(SectionHeading)``;

const NotSectionWrapper = styled(SectionWrapper)<{ hasChildren?: boolean }>`
  padding-top: 4px;
  padding-bottom: 4px;
  ${props =>
    props.hasChildren &&
    css`
      padding-top: 0px;
      padding-bottom: 0px;
      ${NotSectionContent} {
        padding: 0;
        border: none;
      }
      ${NotSectionHeading} {
        margin: 4px 0;
      }
    `}
`;

const ExpressionSpacer = styled.div`
  width: 25px;
`;

const ExpressionSectionButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  max-width: 0;
  transition: max-width 0.2s ease;
`;

const ExpressionSectionContent = styled(SectionContent)<{ isGhost?: boolean }>`
  ${({ isGhost }) =>
    isGhost &&
    css`
      opacity: 0.6;
    `}
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.theme.colors.backgroundActive};

  &:hover ${ExpressionSectionButtonWrapper} {
    max-width: 100%;
  }
`;

const SearchDivAbs = styled.div`
  position: absolute;
  overflow: hidden;
  pointer-events: none;
  height: 400px;
  width: 100%;
`;

const SearchDiv = styled.div`
  width: 100%;
  max-width: 537px;
  min-width: 300px;
  height: 25px;
  position: relative;
`;

const StyledTagSearch = styled(TagSearch)`
  pointer-events: auto;
`;
